'use client';

import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  Text,
  Badge,
  IconButton,
  Tooltip,
  Splitter,
  Button,
  Checkbox,
  toasterApi,
} from '@terra/ui-design-system';
import { getHttp1PseudoHeaderError } from '@terra/shared-utils';
import {
  CircleArrowRight01Icon,
  LinkSquare02Icon,
  LeftToRightListBulletIcon,
  ReplayIcon,
} from '@terra/ui-icons';
import React, { useCallback, useMemo, useState } from 'react';
import {
  useProxyStore,
  type InterceptedRequest,
} from '../../store/proxy-store';
import { useLayoutStore } from '../../store/layoutStore';
import { useElectronAPI } from '../../hooks/useElectronAPI';
import { useHttpReplay } from '../../hooks/useHttpReplay';
import {
  parseBinaryBody,
  decodeBinaryBodyForDisplay,
  encodeDisplayBodyForTransport,
} from '../../electron-shared/utils/binary-body';
import {
  parseRequestFromText,
  parseResponseFromText,
} from '../../utils/httpFormatter';
import { InterceptPane, INTERCEPT_BUTTON_PROPS } from '../InterceptPane';

type SimpleInterceptTabProps = {
  onOpenRules?: () => void;
};

function getRedirectLocationHeaderValue(
  responseHeaders: Record<string, string | string[]> | undefined,
): string | undefined {
  const locationHeader =
    responseHeaders?.['location'] || responseHeaders?.['Location'];

  if (!locationHeader) {
    return undefined;
  }

  return Array.isArray(locationHeader) ? locationHeader[0] : locationHeader;
}

function usePaneSelection({ items }: { items: InterceptedRequest[] }): {
  selectedId: string | null;
  setSelectedId: React.Dispatch<React.SetStateAction<string | null>>;
  selectedItem: InterceptedRequest | undefined;
} {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  React.useEffect(() => {
    if (items.length === 0) {
      setSelectedId(null);

      return;
    }

    if (!selectedId || !items.some((item) => item.id === selectedId)) {
      const firstActionable = items.find((item) => item.status !== 'resumed');

      setSelectedId(firstActionable?.id ?? items[0]?.id ?? null);
    }
  }, [items, selectedId]);
  const selectedItem = items.find((item) => item.id === selectedId);

  return { selectedId, setSelectedId, selectedItem };
}

function createQueueNavigator({
  items,
  selectedId,
  setSelectedId,
}: {
  items: InterceptedRequest[];
  selectedId: string | null;
  setSelectedId: React.Dispatch<React.SetStateAction<string | null>>;
}) {
  return ({ direction }: { direction: 'previous' | 'next' }) => {
    if (items.length === 0) return;
    const actionableItems = items.filter((item) => item.status !== 'resumed');
    const navigableItems = actionableItems.length > 0 ? actionableItems : items;
    const currentIndex = navigableItems.findIndex(
      (item) => item.id === selectedId,
    );
    const startIndex = currentIndex >= 0 ? currentIndex : 0;
    const delta = direction === 'next' ? 1 : -1;
    const nextIndex =
      (startIndex + delta + navigableItems.length) % navigableItems.length;

    setSelectedId(navigableItems[nextIndex]?.id ?? null);
  };
}

export function SimpleInterceptTab({
  onOpenRules,
}: SimpleInterceptTabProps = {}) {
  const {
    interceptQueue,
    removeFromInterceptQueue,
    updateInterceptedRequestText,
    updateInterceptedResponseText,
    updateInterceptedRequestStatus,
    settings,
    updateSettings,
  } = useProxyStore();
  const interceptSplitterSizes = useLayoutStore(
    (state) => state.interceptSplitterSizes,
  );
  const setInterceptSplitterSizes = useLayoutStore(
    (state) => state.setInterceptSplitterSizes,
  );
  const electronAPI = useElectronAPI();
  const { openInReplay } = useHttpReplay();
  const [isProcessing, setIsProcessing] = useState(false);
  const requestQueueItems = useMemo(
    () => interceptQueue.filter((item) => item.type === 'request'),
    [interceptQueue],
  );
  const responseQueueItems = useMemo(
    () => interceptQueue.filter((item) => item.type === 'response'),
    [interceptQueue],
  );
  const {
    selectedId: selectedRequestId,
    setSelectedId: setSelectedRequestId,
    selectedItem: selectedRequestItem,
  } = usePaneSelection({ items: requestQueueItems });
  const {
    selectedId: selectedResponseId,
    setSelectedId: setSelectedResponseId,
    selectedItem: selectedResponseItem,
  } = usePaneSelection({ items: responseQueueItems });
  const navigateRequestQueue = useMemo(
    () =>
      createQueueNavigator({
        items: requestQueueItems,
        selectedId: selectedRequestId,
        setSelectedId: setSelectedRequestId,
      }),
    [requestQueueItems, selectedRequestId, setSelectedRequestId],
  );
  const navigateResponseQueue = useMemo(
    () =>
      createQueueNavigator({
        items: responseQueueItems,
        selectedId: selectedResponseId,
        setSelectedId: setSelectedResponseId,
      }),
    [responseQueueItems, selectedResponseId, setSelectedResponseId],
  );
  const isQueuing =
    settings.interceptEnabled || settings.interceptResponsesEnabled;
  const isRequestEdited = useMemo(() => {
    if (!selectedRequestItem || selectedRequestItem.type !== 'request') {
      return false;
    }

    return (
      selectedRequestItem.originalRequestText !== undefined &&
      selectedRequestItem.requestText !==
        selectedRequestItem.originalRequestText
    );
  }, [selectedRequestItem]);
  const isResponseEdited = useMemo(() => {
    if (!selectedResponseItem || selectedResponseItem.type !== 'response') {
      return false;
    }

    return (
      selectedResponseItem.originalResponseText !== undefined &&
      selectedResponseItem.responseText !==
        selectedResponseItem.originalResponseText
    );
  }, [selectedResponseItem]);
  const isRedirectResponse = useMemo(() => {
    if (!selectedResponseItem || selectedResponseItem.type !== 'response') {
      return false;
    }

    const status = selectedResponseItem.request.status;

    return status !== undefined && [301, 302, 303, 307, 308].includes(status);
  }, [selectedResponseItem]);
  const handleToggleQueuing = useCallback(() => {
    if (isQueuing) {
      updateSettings({
        interceptEnabled: false,
        interceptResponsesEnabled: false,
      });

      return;
    }

    updateSettings({
      interceptEnabled: true,
      interceptResponsesEnabled: settings.interceptResponsesEnabled,
    });
  }, [isQueuing, settings.interceptResponsesEnabled, updateSettings]);
  const forwardItem = useCallback(
    async ({ item }: { item: InterceptedRequest }) => {
      if (isProcessing) return;
      setIsProcessing(true);

      try {
        if (item.type === 'request') {
          const wasModified =
            item.originalRequestText !== undefined &&
            item.requestText !== item.originalRequestText;

          if (wasModified) {
            const pseudoHeaderError = getHttp1PseudoHeaderError({
              requestText: item.requestText,
            });

            if (pseudoHeaderError) {
              throw new Error(pseudoHeaderError);
            }

            const parsedRequest = parseRequestFromText({
              text: item.requestText,
              originalUrl: item.request.url,
            });

            if (!parsedRequest) {
              throw new Error('Failed to parse intercepted request');
            }

            const isOriginallyBinaryRequest =
              parseBinaryBody(item.request.body).bodyEncoding === 'base64' ||
              item.request.bodyContentType === 'binary' ||
              item.request.bodyContentType === 'multipart';
            const bodyToSend =
              isOriginallyBinaryRequest && parsedRequest.body
                ? encodeDisplayBodyForTransport(parsedRequest.body)
                : parsedRequest.body;

            await electronAPI.resumeInterceptedRequest(item.id, {
              method: parsedRequest.method,
              url: parsedRequest.url,
              headers: parsedRequest.headers,
              body: bodyToSend,
            });
          } else {
            await electronAPI.resumeInterceptedRequest(item.id);
          }

          if (settings.interceptResponsesEnabled) {
            updateInterceptedRequestStatus(item.id, 'resumed');
            const currentIndex = requestQueueItems.findIndex(
              (queueItem) => queueItem.id === item.id,
            );
            const nextItem = requestQueueItems.find(
              (queueEntry, index) =>
                index > currentIndex && queueEntry.status !== 'resumed',
            );

            if (nextItem) {
              setSelectedRequestId(nextItem.id);
            }
          } else {
            removeFromInterceptQueue(item.id);
          }

          return;
        }

        const wasModified = item.responseText !== item.originalResponseText;

        if (wasModified) {
          const parsedResponse = parseResponseFromText({
            text: item.responseText || '',
          });

          if (!parsedResponse) {
            throw new Error('Failed to parse intercepted response');
          }

          await electronAPI.resumeInterceptedResponse(item.id, {
            statusCode: parsedResponse.statusCode,
            statusMessage: parsedResponse.statusMessage,
            headers: parsedResponse.headers,
            body: parsedResponse.body,
          });
        } else {
          await electronAPI.resumeInterceptedResponse(item.id);
        }

        removeFromInterceptQueue(item.id);
      } catch (error) {
        console.error('Failed to forward item:', error);
        toasterApi.error({
          title: 'Failed to forward',
          description:
            error instanceof Error
              ? error.message
              : 'Could not forward the intercepted item.',
        });
      } finally {
        setIsProcessing(false);
      }
    },
    [
      isProcessing,
      electronAPI,
      settings.interceptResponsesEnabled,
      updateInterceptedRequestStatus,
      removeFromInterceptQueue,
      requestQueueItems,
      setSelectedRequestId,
    ],
  );
  const dropItem = useCallback(
    async ({ item }: { item: InterceptedRequest }) => {
      if (isProcessing) return;
      setIsProcessing(true);

      try {
        if (item.type === 'request') {
          await electronAPI.dropInterceptedRequest(item.id);
        } else {
          await electronAPI.dropInterceptedResponse(item.id);
        }

        removeFromInterceptQueue(item.id);
      } catch (error) {
        console.error('Failed to drop item:', error);
      } finally {
        setIsProcessing(false);
      }
    },
    [isProcessing, electronAPI, removeFromInterceptQueue],
  );
  const handleFollowRedirect = useCallback(async () => {
    if (!selectedResponseItem || !isRedirectResponse || isProcessing) return;
    setIsProcessing(true);

    try {
      const redirectUrl = getRedirectLocationHeaderValue(
        selectedResponseItem.request.responseHeaders,
      );

      if (!redirectUrl) {
        console.error('No Location header found in redirect response');

        return;
      }

      await electronAPI.resumeInterceptedResponse(selectedResponseItem.id);
      removeFromInterceptQueue(selectedResponseItem.id);
      window.open(redirectUrl, '_blank');
    } catch (error) {
      console.error('Failed to follow redirect:', error);
    } finally {
      setIsProcessing(false);
    }
  }, [
    selectedResponseItem,
    isRedirectResponse,
    isProcessing,
    electronAPI,
    removeFromInterceptQueue,
  ]);
  const handleSendToReplay = useCallback(
    ({ item }: { item: InterceptedRequest }) => {
      try {
        if (item.type === 'response') {
          const { body, bodyEncoding } = parseBinaryBody(
            item.request.body || '',
          );

          openInReplay({
            method: item.request.method,
            url: item.request.url,
            headers: item.request.headers,
            rawHeaders: item.request.rawHeaders,
            body,
            bodyEncoding,
            httpVersion: item.request.httpVersion,
          });

          return;
        }

        const parsedRequest = parseRequestFromText({
          text: item.requestText,
          originalUrl: item.request.url,
        });

        if (!parsedRequest) {
          throw new Error('Failed to parse intercepted request');
        }

        const { body, bodyEncoding } = parseBinaryBody(
          parsedRequest.body || '',
        );

        openInReplay({
          method: parsedRequest.method,
          url: parsedRequest.url,
          headers: parsedRequest.headers,
          rawHeaders: item.request.rawHeaders,
          body,
          bodyEncoding,
          httpVersion: parsedRequest.httpVersion,
        });
      } catch (error) {
        console.error('Failed to send request to replay:', error);
        toasterApi.error({
          title: 'Failed to send to replay',
          description:
            error instanceof Error
              ? error.message
              : 'Could not parse the intercepted request.',
        });
      }
    },
    [openInReplay],
  );
  const requestEditorValue = selectedRequestItem
    ? decodeBinaryBodyForDisplay(selectedRequestItem.requestText)
    : '';
  const responseEditorValue = selectedResponseItem
    ? decodeBinaryBodyForDisplay(selectedResponseItem.responseText || '')
    : '';

  return (
    <VStack h="full" w="full" gap="0" overflow="hidden">
      <HStack
        w="full"
        px="2"
        py="1.5"
        borderBottomWidth="1px"
        borderColor="border.default"
        bg="bg.subtle"
        gap="2"
        alignItems="center"
        flexShrink={0}
        flexWrap="wrap"
      >
        <Button
          {...INTERCEPT_BUTTON_PROPS}
          variant={isQueuing ? 'solid' : 'outline'}
          colorPalette={isQueuing ? 'orange' : 'gray'}
          onClick={handleToggleQueuing}
        >
          {isQueuing ? 'Queuing' : 'Forwarding'}
        </Button>

        <Checkbox
          size="sm"
          checked={settings.interceptEnabled}
          onCheckedChange={(details) =>
            updateSettings({ interceptEnabled: details.checked === true })
          }
        >
          <Text fontSize="xs">Requests</Text>
        </Checkbox>

        <Checkbox
          size="sm"
          checked={settings.interceptResponsesEnabled}
          onCheckedChange={(details) =>
            updateSettings({
              interceptResponsesEnabled: details.checked === true,
            })
          }
        >
          <Text fontSize="xs">Responses</Text>
        </Checkbox>

        <Checkbox size="sm" checked={false} disabled>
          <Text fontSize="xs" color="fg.muted">
            Websockets
          </Text>
        </Checkbox>

        <Box flex="1" />

        <Badge variant="emphasis" size="sm">
          {requestQueueItems.length} req / {responseQueueItems.length} res
        </Badge>

        <Tooltip title="Send selected item to Replay">
          <IconButton
            aria-label="Send to Replay"
            size="xs"
            variant="ghost"
            onClick={() => {
              const item = selectedRequestItem ?? selectedResponseItem;

              if (item) {
                handleSendToReplay({ item });
              }
            }}
            disabled={!selectedRequestItem && !selectedResponseItem}
          >
            <ReplayIcon size={12} />
          </IconButton>
        </Tooltip>

        <Tooltip title="Follow redirect and open in new tab">
          <IconButton
            aria-label="Follow redirect"
            size="xs"
            variant="ghost"
            onClick={handleFollowRedirect}
            disabled={
              !selectedResponseItem || !isRedirectResponse || isProcessing
            }
          >
            <CircleArrowRight01Icon size={12} />
          </IconButton>
        </Tooltip>

        {onOpenRules && (
          <Tooltip title="Manage Rules">
            <IconButton
              aria-label="Manage Rules"
              size="xs"
              variant="ghost"
              onClick={onOpenRules}
            >
              <LeftToRightListBulletIcon size={12} />
            </IconButton>
          </Tooltip>
        )}
      </HStack>

      {isRedirectResponse && selectedResponseItem && (
        <HStack
          w="full"
          px="3"
          py="1"
          bg="bg.muted"
          borderBottomWidth="1px"
          borderColor="border.subtle"
          gap="2"
          flexShrink={0}
        >
          <LinkSquare02Icon size={12} />
          <Text fontSize="xs" color="fg.muted">
            Redirects to:{' '}
            {getRedirectLocationHeaderValue(
              selectedResponseItem.request.responseHeaders,
            )}
          </Text>
        </HStack>
      )}

      <Splitter.Root
        defaultSize={interceptSplitterSizes.map(({ size }) => size)}
        panels={interceptSplitterSizes.map(({ id, minSize }) => ({
          id: id.toString(),
          minSize,
        }))}
        orientation="horizontal"
        style={{ flex: 1, overflow: 'hidden' }}
        onResizeEnd={({ size }) => {
          setInterceptSplitterSizes(
            interceptSplitterSizes.map((panel, index) => ({
              ...panel,
              size: size[index],
            })),
          );
        }}
      >
        <Splitter.Panel id="request" borderWidth="0" w="full" h="full">
          <InterceptPane
            kind="request"
            items={requestQueueItems}
            selectedId={selectedRequestId}
            onSelect={({ id }) => setSelectedRequestId(id)}
            selectedItem={selectedRequestItem}
            editorValue={requestEditorValue}
            onEditorChange={
              selectedRequestItem?.type === 'request' &&
              selectedRequestItem.status !== 'resumed'
                ? ({ value }) =>
                    updateInterceptedRequestText(selectedRequestItem.id, value)
                : undefined
            }
            editorReadOnly={
              !selectedRequestItem ||
              selectedRequestItem.type !== 'request' ||
              selectedRequestItem.status === 'resumed'
            }
            isEdited={isRequestEdited}
            isProcessing={isProcessing}
            onForward={() => {
              if (selectedRequestItem) {
                void forwardItem({ item: selectedRequestItem });
              }
            }}
            onDrop={() => {
              if (selectedRequestItem) {
                void dropItem({ item: selectedRequestItem });
              }
            }}
            emptyTableMessage={
              settings.interceptEnabled
                ? 'No intercepted requests yet'
                : 'Enable request interception to start'
            }
            emptyEditorMessage={
              settings.interceptEnabled
                ? 'Select a request from the queue'
                : 'Enable request interception to start'
            }
            onNavigate={navigateRequestQueue}
          />
        </Splitter.Panel>

        <Splitter.ResizeTrigger
          id="request:response"
          width="1px"
          minWidth="1px"
          bg="border.subtle"
          _hover={{
            bg: 'border.default',
          }}
        />

        <Splitter.Panel id="response" borderWidth="0" w="full" h="full">
          <InterceptPane
            kind="response"
            items={responseQueueItems}
            selectedId={selectedResponseId}
            onSelect={({ id }) => setSelectedResponseId(id)}
            selectedItem={selectedResponseItem}
            editorValue={responseEditorValue}
            onEditorChange={
              selectedResponseItem?.type === 'response'
                ? ({ value }) =>
                    updateInterceptedResponseText(
                      selectedResponseItem.id,
                      value,
                    )
                : undefined
            }
            editorReadOnly={!selectedResponseItem}
            editorStatusCode={selectedResponseItem?.request.status}
            isEdited={isResponseEdited}
            isProcessing={isProcessing}
            onForward={() => {
              if (selectedResponseItem) {
                void forwardItem({ item: selectedResponseItem });
              }
            }}
            onDrop={() => {
              if (selectedResponseItem) {
                void dropItem({ item: selectedResponseItem });
              }
            }}
            emptyTableMessage={
              settings.interceptResponsesEnabled
                ? 'No intercepted responses yet'
                : 'Enable response interception to start'
            }
            emptyEditorMessage={
              settings.interceptResponsesEnabled
                ? 'Select a response from the queue'
                : 'Enable response interception to start'
            }
            onNavigate={navigateResponseQueue}
          />
        </Splitter.Panel>
      </Splitter.Root>
    </VStack>
  );
}
